import { RawLocation, Route, Location } from 'vue-router';
import { store } from '@/store';
import { AuthenticatedUser } from '@/application/authentication/types';
import { UserRole } from '@/helpers/data';

function getRedirectRouteForAuthenticatedUser(): Location {
  return { name: 'default' };
}

function getRedirectRouteForUnauthenticatedUser(): Location {
  return { name: 'root' };
}

function getInheritedAccessibleRolesByRole(role: UserRole): UserRole[] {
  switch (role) {
    case 'ROLE_ADMIN':
      return [
        'ROLE_ADMIN'
      ];
    case 'ROLE_USER':
      return [
        'ROLE_ADMIN',
        'ROLE_USER',
      ];
    default:
      return [];
  }
}

export function accessGuard(
  to: Route,
  from: Route,
  next: (to?: RawLocation | false | ((vm: any) => any) | void) => void
) {
  const isAuthenticated: boolean = store.state.authentication.isAuthenticated;
  const user: AuthenticatedUser = store.state.authentication.user;

  // Route is always accessible
  if (to.meta.alwaysAccessible) {
    next();
    return;
  }

  // User is authenticated but route is just accessible as unauthenticated user
  if (isAuthenticated && !to.meta.requiresAuth) {
    next(getRedirectRouteForAuthenticatedUser());
    return;
  }

  // User is unauthenticated but route is just accessible as authenticated user
  if (!isAuthenticated && to.meta.requiresAuth) {
    next(getRedirectRouteForUnauthenticatedUser());
    return;
  }

  // If user role is required to access the route
  if (to.meta.requiresRole) {
    const accessibleRoles = getInheritedAccessibleRolesByRole(to.meta.requiresRole);
    const userHasRole = accessibleRoles.filter((accessibleRole) => user.roles.includes(accessibleRole)).length > 0;

    // If user doesn't have required role and is authenticated
    if (!userHasRole && isAuthenticated) {
      next(getRedirectRouteForAuthenticatedUser());
      return;
    }

    // If user doesn't have required role and is unauthenticated
    if (!userHasRole && !isAuthenticated) {
      next(getRedirectRouteForUnauthenticatedUser());
      return;
    }
  }

  // If feature is required to access the route
  if (to.meta.requiresFeature) {
    const userHasAccessToFeature = user.enabledFeatures.includes(to.meta.requiresFeature);

    // If user doesn't have access to feature and is authenticated
    if (!userHasAccessToFeature && isAuthenticated) {
      next(getRedirectRouteForAuthenticatedUser());
      return;
    }

    // If user doesn't have access to feature and is unauthenticated
    if (!userHasAccessToFeature && !isAuthenticated) {
      next(getRedirectRouteForUnauthenticatedUser());
      return;
    }
  }

  next();
}
